import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getTournamentPreds, setTournamentPred } from '../api';
import { useAuth } from '../auth';
import PageHeader from '../components/PageHeader';
import ForecastPanel from '../components/ForecastPanel';
import { toast } from '../toast';
import { haptic } from '../haptic';

const TEAMS: { code: string; name: string; flag: string }[] = [
  { code: 'ARG', name: 'Argentina', flag: '🇦🇷' }, { code: 'BRA', name: 'Brazil', flag: '🇧🇷' },
  { code: 'ESP', name: 'Spain', flag: '🇪🇸' }, { code: 'FRA', name: 'France', flag: '🇫🇷' },
  { code: 'GER', name: 'Germany', flag: '🇩🇪' }, { code: 'ENG', name: 'England', flag: '🏴󠁧󠁢󠁥󠁮󠁧󠁿' },
  { code: 'POR', name: 'Portugal', flag: '🇵🇹' }, { code: 'NED', name: 'Netherlands', flag: '🇳🇱' },
  { code: 'BEL', name: 'Belgium', flag: '🇧🇪' }, { code: 'CRO', name: 'Croatia', flag: '🇭🇷' },
  { code: 'URU', name: 'Uruguay', flag: '🇺🇾' }, { code: 'MAR', name: 'Morocco', flag: '🇲🇦' },
  { code: 'COL', name: 'Colombia', flag: '🇨🇴' }, { code: 'MEX', name: 'Mexico', flag: '🇲🇽' },
  { code: 'JPN', name: 'Japan', flag: '🇯🇵' }, { code: 'KOR', name: 'South Korea', flag: '🇰🇷' },
  { code: 'USA', name: 'USA', flag: '🇺🇸' }, { code: 'CAN', name: 'Canada', flag: '🇨🇦' },
  { code: 'SEN', name: 'Senegal', flag: '🇸🇳' }, { code: 'CIV', name: "Côte d'Ivoire", flag: '🇨🇮' },
];

export default function TournamentPreds() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['t-preds'], queryFn: getTournamentPreds });

  const mine = data?.items.find((p) => p.user === user) ?? null;
  const [champ, setChamp] = useState('');
  const [runner, setRunner] = useState('');
  const [scorer, setScorer] = useState('');
  const [dark, setDark] = useState('');
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    setChamp(mine?.champion ?? '');
    setRunner(mine?.runner_up ?? '');
    setScorer(mine?.top_scorer ?? '');
    setDark(mine?.dark_horse ?? '');
  }, [mine]);

  const mut = useMutation({
    mutationFn: () => setTournamentPred({ champion: champ || null, runner_up: runner || null, top_scorer: scorer || null, dark_horse: dark || null }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['t-preds'] }); setMsg('Saved.'); setTimeout(() => setMsg(null), 1500); toast.success('Tournament picks saved.'); haptic.success(); },
    onError: (e: Error) => { setMsg(e.message); toast.error(e.message); haptic.error(); },
  });

  return (
    <>
      <PageHeader title={'Tournament predictions'} subtitle="Predict the tournament outcomes. Locks at first kickoff." />
      <div className="px-4 lg:px-0 space-y-4">
        {data?.locked && !user && (
          <div className="card p-4 text-sm text-dim">Tournament has started — predictions are locked. See everyone's picks below.</div>
        )}
        {user && (
          <form
            onSubmit={(e) => { e.preventDefault(); mut.mutate(); }}
            className="card p-4 space-y-3"
          >
            <TeamSelect label={'Champion'} value={champ} setValue={setChamp} disabled={!!data?.locked} />
            <TeamSelect label={'Runner-up'} value={runner} setValue={setRunner} disabled={!!data?.locked} />
            <label className="block">
              <span className="text-[10px] uppercase tracking-wider text-dim mb-1 block">{'Top scorer'}</span>
              <input
                type="text" value={scorer} onChange={(e) => setScorer(e.target.value)}
                placeholder="e.g. Mbappé"
                disabled={!!data?.locked}
                className="w-full bg-ink-800 border border-ink-700 rounded-lg px-3 py-2 text-sm text-chalk"
              />
            </label>
            <TeamSelect label={'Dark horse'} value={dark} setValue={setDark} disabled={!!data?.locked} />
            {msg && <div className="text-xs text-pitch-400">{msg}</div>}
            <button
              type="submit"
              disabled={mut.isPending || !!data?.locked}
              className="w-full h-10 rounded-lg bg-pitch-500 text-ink-950 font-semibold text-sm disabled:opacity-50"
            >{mine ? 'Update' : 'Submit'}</button>
          </form>
        )}
        <ForecastPanel />

        <section>
          <h2 className="text-xs uppercase tracking-widest text-dim mb-2 px-4 lg:px-0">League picks</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {(data?.items ?? []).map((p) => (
              <div key={p.user} className="card p-3 text-xs">
                <div className="font-semibold text-sm mb-1">{p.user}</div>
                <Row label={'Champion'} value={TEAMS.find((x) => x.code === p.champion) ? `${TEAMS.find((x) => x.code === p.champion)?.flag} ${p.champion}` : p.champion || '—'} />
                <Row label={'Runner-up'} value={TEAMS.find((x) => x.code === p.runner_up) ? `${TEAMS.find((x) => x.code === p.runner_up)?.flag} ${p.runner_up}` : p.runner_up || '—'} />
                <Row label={'Top scorer'} value={p.top_scorer || '—'} />
                <Row label={'Dark horse'} value={TEAMS.find((x) => x.code === p.dark_horse) ? `${TEAMS.find((x) => x.code === p.dark_horse)?.flag} ${p.dark_horse}` : p.dark_horse || '—'} />
              </div>
            ))}
          </div>
        </section>
      </div>
    </>
  );
}

function TeamSelect({ label, value, setValue, disabled }: { label: string; value: string; setValue: (v: string) => void; disabled?: boolean }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-wider text-dim mb-1 block">{label}</span>
      <select
        value={value} onChange={(e) => setValue(e.target.value)} disabled={disabled}
        className="w-full bg-ink-800 border border-ink-700 rounded-lg px-3 py-2 text-sm text-chalk"
      >
        <option value="">—</option>
        {TEAMS.map((t) => <option key={t.code} value={t.code}>{t.flag} {t.name}</option>)}
      </select>
    </label>
  );
}
function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-2 text-[11px]">
      <span className="text-dim">{label}</span>
      <span>{value}</span>
    </div>
  );
}
